<!--
  employeeCardInstitutions: assigned and planned Standorte of a teacher as field rows (employee card dialog). Same guards,
  links and dialogs as the former assigned/plannedInstitutionsListItem. The card root guards (read employees
  assignedInstitutions / plannedInstitutions) stay on the rows themselves.
  Props: employee
-->
<template>
  <div>
    <div v-for="group in groups" :key="group.key" v-can:read-employees="{ id: employee._id, keys: [group.key] }" class="fld">
      <span class="fld__icon"><v-icon>{{ group.icon }}</v-icon></span>
      <div style="min-width: 0">
        <div class="fld__label">{{ group.label }}<span v-if="group.items.length" class="ml-1">({{ group.items.length }})</span></div>
        <div v-if="!group.items.length" class="fld__value fld__value--muted">{{ group.empty }}</div>
        <div v-else class="chips">
          <v-menu v-for="item in group.items" :key="String(item._id)" offset-y>
            <template v-slot:activator="{ on }">
              <button class="chip" type="button" v-on="on">
                <v-icon x-small>mdi-domain</v-icon>
                <span v-can:read-institutions="{ id: item._id, keys: ['name'] }">{{ getInstitutionNameWithSateCode(item) }}</span>
              </button>
            </template>
            <v-list v-can:read-institutions="{ id: item._id }" class="soft-menu py-1" dense>
              <v-list-item @click="show(item._id)">
                <v-list-item-icon><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Standort ansehen</v-list-item-title>
              </v-list-item>
              <v-list-item :href="$router.resolve(`/institutions/${item._id}`).href" target="_blank">
                <v-list-item-icon><v-icon small>mdi-open-in-new</v-icon></v-list-item-icon>
                <v-list-item-title>Standort in neuem Tab öffnen</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
      <div class="fld__actions">
        <button v-if="group.items.length" v-can:readAndUpdate-employees="{ id: employee._id, keys: [group.key] }"
                :aria-label="group.editTitle" class="ibtn" type="button" v-tip="group.editTitle" @click="group.open()">
          <v-icon>mdi-pencil-outline</v-icon>
        </button>
        <button v-else :aria-label="group.addTitle" class="ibtn ibtn--add" type="button" v-tip="group.addTitle" @click="group.open()">
          <v-icon>mdi-plus</v-icon>
        </button>
      </div>
    </div>

    <!-- DIALOGS -->
    <institution-card-dialog v-if="activeInstitutionId" v-model="showInstitutionCardDialog" :institution-id="activeInstitutionId"/>
    <assign-institutions-dialog v-model="showAssignInstitutionsDialog" :employee="employee"/>
    <plan-institutions-dialog v-model="showPlanInstitutionsDialog" :employee="employee"/>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import mongoose from 'mongoose';
import {Employee} from '@/interfaces/employee.interface';
import {Institution} from '@/interfaces/institution.interface';
import InstitutionCardDialog from '@/components/common/profile/institutionCardDialog.vue';
import AssignInstitutionsDialog from '@/components/employees/employeeView/profileView/assignInstitutionsDialog.vue';
import PlanInstitutionsDialog from '@/components/employees/employeeView/profileView/planInstitutionsDialog.vue';
import {getInstitutionNameWithSateCode} from '@/helpers/getInstitutionNameWithSateCode';

type InstitutionItem = Pick<Institution, 'name' | '_id'>;

interface InstitutionGroup {
  key: 'assignedInstitutions' | 'plannedInstitutions';
  label: string;
  icon: string;
  empty: string;
  editTitle: string;
  addTitle: string;
  items: InstitutionItem[];
  open: () => void;
}

export default Vue.extend({
  components: {InstitutionCardDialog, AssignInstitutionsDialog, PlanInstitutionsDialog},
  props: {
    employee: {
      type: Object as () => Employee,
      required: true,
    },
  },
  data: () => ({
    activeInstitutionId: null as null | mongoose.Types.ObjectId,
    showInstitutionCardDialog: false,
    showAssignInstitutionsDialog: false,
    showPlanInstitutionsDialog: false,
  }),
  computed: {
    groups(): InstitutionGroup[] {
      const employee = this.employee as unknown as Record<string, InstitutionItem[] | undefined>;
      const groups: InstitutionGroup[] = [];
      if (employee.assignedInstitutions) {
        groups.push({
          key: 'assignedInstitutions',
          label: 'Zugeteilte Standorte',
          icon: 'mdi-domain',
          empty: 'Kein Standort zugeteilt',
          editTitle: 'Zuteilungen bearbeiten',
          addTitle: 'Standort zuweisen',
          items: employee.assignedInstitutions,
          open: () => (this.showAssignInstitutionsDialog = true),
        });
      }
      if (employee.plannedInstitutions) {
        groups.push({
          key: 'plannedInstitutions',
          label: 'Vorgemerkte Standorte',
          icon: 'mdi-domain-plus',
          empty: 'Kein Standort vorgemerkt',
          editTitle: 'Vormerkungen bearbeiten',
          addTitle: 'Standort vormerken',
          items: employee.plannedInstitutions,
          open: () => (this.showPlanInstitutionsDialog = true),
        });
      }
      return groups;
    },
  },
  methods: {
    getInstitutionNameWithSateCode,
    show(id: mongoose.Types.ObjectId) {
      this.activeInstitutionId = id;
      this.showInstitutionCardDialog = true;
    },
  },
});
</script>

<style src="@/components/acquisitionList/ui/itemDetails.css"></style>
<style src="@/components/common/modern/modern.css"></style>
